import LoaderImg from "@assets/images/loader.gif";
import { CLOUDINARY_INTEL_BASE_URL, ERROR_MSG } from "@utils/enum";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { getInitials, generateTrackingObject } from "@core/global/global-country-functions";
import { getTitleCase } from "@utils/utils";
import { getUserActivePlan } from "@helpers/profileHelper";
import { Modal } from "react-bootstrap";
import { getApiUrl } from "@utils/baseUrl";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { useSelector } from "react-redux";
import commaFormat from "@utils/commaFormat";
import { captureException } from "@sentry/react";

const DueDiligenceReports = ({ countDetails }) => {
  const [loader, setLoader] = useState(true);
  const [userAndOrgChecks, setUserAndOrgChecks] = useState({ hasOrgRequestedReport: false, hasUserRequestedReport: false });
  const [requestorDetails, setRequestorDetails] = useState({});
  const [reportDetails, setReportDetails] = useState({});
  const [creditsLeft, setCreditsLeft] = useState({ userCredits: null, orgCredits: null });
  const [status, setStatus] = useState("");
  const [openPopup, setOpenPopup] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const { search: searchUrlParams, pathname } = useLocation();
  const dataSource = new URLSearchParams(searchUrlParams).get("dataSource");
  const { buyerCaching, supplierCaching } = useSelector((state) => state["companyProfile"]);
  const _id = buyerCaching?.profileDetails?.elastic_data_id?.[0] || supplierCaching?.profileDetails?.elastic_data_id?.[0];
  const urlArray = pathname.split("/");
  let entityId = urlArray[`${urlArray.length - 1}`];
  let slug = urlArray[`${urlArray.length - 2}`];
  let isDelivered = status === "delivered";

  const onRequest = () => {
    setLoader(true);
    trademoTrackingEvents(trackingEventDictionary.companyProfileDueDiligenceReportRequestReport, generateTrackingObject(dataSource, pathname, getUserActivePlan()));
    axios
      .post(getApiUrl("requestDueDiligenceReport"), {
        company_id: entityId,
        slug: slug,
        _id: _id,
      })
      .then((res) => {
        if (res && res.data && res.status === 200) {
          setReportDetails(res.data.report_details);
          setRequestorDetails(res.data.requestor_details);
          setStatus(res.data.status);
          setCreditsLeft({
            userCredits: res.data?.credits_left.user_available_credits === null ? 0 : res.data?.credits_left.user_available_credits,
            orgCredits: res.data?.credits_left.organisation_available_credits === null ? 0 : res.data?.credits_left.organisation_available_credits,
          });
          setUserAndOrgChecks({ hasOrgRequestedReport: res.data.is_org_req, hasUserRequestedReport: res.data.is_user_req });
        }
      })
      .catch((err) => {
        captureException(err);
        toast.error(err?.response?.data?.message || ERROR_MSG);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const getInTouch = (text) => {
    return (
      <Modal
        show={openPopup}
        onHide={() => {
          setOpenPopup(false);
        }}
        keyboard={false}
        centered
        className="trademo-modal company-profile-get-in-touch-modal">
        <Modal.Header>
          <Modal.Title>
            <img src={`${CLOUDINARY_INTEL_BASE_URL}/v1684478309/intel/icons/Lightning-alt_t9mkjb.svg`} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{text}</Modal.Body>
        <Modal.Footer className="justify-content-end">
          <button type="button" className="btn btn-secondary" onClick={() => setOpenPopup(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary trademo-new-btn"
            onClick={() => {
              getInTouchForCredits();
              setOpenPopup(false);
            }}>
            Get in Touch
          </button>
        </Modal.Footer>
      </Modal>
    );
  };
  const onCheck = () => {
    if (creditsLeft.userCredits === 0 || creditsLeft.orgCredits === 0) {
      setOpenPopup(true);
    } else {
      if (creditsLeft?.userCredits < 500) {
        return toast.error("You do not have enough Value Add Credit(s) to request the report. Please reach out to customersuccess@trademo.com for more credits.");
      } else {
        trademoTrackingEvents(trackingEventDictionary.companyProfileDueDiligenceReportRequestNewReport, generateTrackingObject(dataSource, pathname, getUserActivePlan()));
        setConfirmationModal(true);
      }
    }
  };

  const getStatus = () => {
    setLoader(true);
    axios
      .post(getApiUrl("getDueDiligenceReportStatus"), {
        company_id: entityId,
      })
      .then((res) => {
        if (res && res.data && res.status === 200) {
          setReportDetails(res.data.report_details);
          setRequestorDetails(res.data.requestor_details);
          setStatus(res.data?.status);
          setCreditsLeft({
            userCredits: res.data?.credits_left.user_available_credits === null ? 0 : res.data?.credits_left.user_available_credits,
            orgCredits: res.data?.credits_left.organisation_available_credits === null ? 0 : res.data?.credits_left.organisation_available_credits,
          });
          setUserAndOrgChecks({ hasOrgRequestedReport: res.data.is_org_req, hasUserRequestedReport: res.data.is_user_req });
        } else {
          toast.error(res.data.message);
        }
      })
      .catch((err) => {
        captureException(err);
        toast.error("Something went wrong.");
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const pdfFileOpener = (req_id, cp_id, _type) => {
    let requestId = req_id ? req_id : reportDetails.request_id;
    let companyId = cp_id ? cp_id : reportDetails.company_id;
    let type = _type;
    if (req_id === "sample") {
      trademoTrackingEvents(trackingEventDictionary.companyProfileDueDiligenceReportViewSampleReport, generateTrackingObject(dataSource, pathname, getUserActivePlan()));
    }
    if (_type === "pdf" && req_id !== "sample") {
      trademoTrackingEvents(trackingEventDictionary.companyProfileDueDiligenceReportDownloadPdf, generateTrackingObject(dataSource, pathname, getUserActivePlan()));
    } else if (_type === "excel" && req_id !== "sample") {
      trademoTrackingEvents(trackingEventDictionary.companyProfileDueDiligenceReportDownloadExcel, generateTrackingObject(dataSource, pathname, getUserActivePlan()));
    }
    axios
      .post(getApiUrl("downloadDueDeligenceReport"), {
        company_id: companyId,
        request_id: requestId,
        type: type,
      })
      .then((res) => {
        if (res && res.data && res.status === 200) {
          window.open(res.data.file_url, "_blank");
        }
      })
      .catch((err) => {
        captureException(err);
        toast.error("Something went wrong. Please try again later.");
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const getInTouchForCredits = () => {
    setLoader(true);
    axios
      .post(getApiUrl("notifyDdrCreditRequest"), {
        // "company_id": entityId,
      })
      .then((res) => {
        if (res && res.data && res.status === 200) {
          toast.success("Your request for Due Dilligence Reports credits has been successfully submitted");
        } else {
          toast.error(res.data.message);
        }
      })
      .catch((err) => {
        captureException(err);
        toast.error("Something went wrong.");
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    getStatus();
  }, []);

  const dueDiligenceFeatures = ["Shareholder and Key Personnel information", "Financial and credit analysis", "Legal and compliance checks", "Certifications and Ratings", "Ownership and management details"];
  const creditsText = {
    user: "You seem to have reached your usage limit for requesting Due Diligence Reports. Reach out to your Account Administrator to extend your usage limit.",
    organisation: "Your organisation do not seem to have any Diligence Report Credits. Reach out to customer success for credits.",
  };

  let isSampleReport = true;

  return (
    <>
      {loader && (
        <div className="tw-h-[calc(100vh-240px)] tw-flex tw-items-center tw-justify-center">
          <img src={LoaderImg} alt="description of gif" />
        </div>
      )}
      {creditsLeft.orgCredits !== 0 && creditsLeft.userCredits !== 0 && !userAndOrgChecks.hasOrgRequestedReport && !userAndOrgChecks.hasUserRequestedReport && !loader ? (
        <div className="tw-flex tw-flex-col tw-gap-2 tw-pt-2 tw-px-3">
          {isSampleReport && (
            <div className="tw-flex tw-items-start tw-justify-between tw-py-3">
              <span className="tw-text-sm tw-font-medium tw-max-w-[65%]">Trademo delivers due diligence reports that provide accurate insights empowering informed decisions across global trade.</span>
              <div className="tw-py-0 tw-px-2.5 tw-text-xs tw-bg-remaining-credits tw-font-bold tw-rounded-none">
                <div className="float-right">
                  <img className="px-2 py-1" src="https://res.cloudinary.com/ddfycftwg/image/upload/v1641874517/images/icons/Iconly_Bold_Info_Circle_bwgt7p.svg" alt="..." />
                  <span>Remaining Value Add Credit(s) : </span>
                  <span className="pr-3">{commaFormat(creditsLeft?.userCredits)}</span>
                </div>
              </div>
            </div>
          )}
          <div className="tw-w-full tw-bg-gray-200 tw-border-t"></div>
          <div className="tw-flex tw-gap-5 tw-h-fit tw-justify-center">
            <div className="tw-w-1/2 tw-flex tw-flex-col tw-rounded-lg tw-justify-center tw-px-6 tw-py-[14px] tw-gap-3">
              <div className="tw-flex tw-flex-col tw-gap-3">
                <span className="tw-text-sm tw-font-medium tw-flex tw-flex-col">
                  <span>
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#5a1ce2">
                      <path d="M852-212 732-332l56-56 120 120-56 56ZM708-692l-56-56 120-120 56 56-120 120Zm-456 0L132-812l56-56 120 120-56 56ZM108-212l-56-56 120-120 56 56-120 120Zm125 92 65-281L80-590l288-25 112-265 112 265 288 25-218 189 65 281-247-149-247 149Z" />
                    </svg>
                  </span>
                  <span className="tw-text-base tw-font-semibold">Key Highlights:</span>
                </span>
                <div className="tw-w-full tw-bg-gray-200 tw-border-t"></div>
                {dueDiligenceFeatures.map((item) => {
                  return (
                    <span key={item} className="tw-flex tw-gap-2 tw-items-center">
                      <span>
                        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#101828">
                          <path d="m344-60-76-128-144-32 14-148-98-112 98-112-14-148 144-32 76-128 136 58 136-58 76 128 144 32-14 148 98 112-98 112 14 148-144 32-76 128-136-58-136 58Zm94-278 226-226-56-58-170 170-86-84-56 56 142 142Z" />
                        </svg>
                      </span>
                      <span className="tw-text-sm tw-font-normal tw-text-gray-900">{item}</span>
                    </span>
                  );
                })}
                <div className="tw-flex tw-justify-center tw-gap-3">
                  <span className="tw-flex tw-gap-1 tw-items-center tw-cursor-pointer tw-border tw-border-gray-200 tw-text-gray-900 tw-text-sm tw-bg-white tw-shadow-button tw-font-semibold tw-rounded-xl tw-px-4 tw-py-2" onClick={() => pdfFileOpener("sample", "pdf", "pdf")}>
                    <span className="material-symbols-outlined tw-w-5">picture_as_pdf</span>
                    View Sample Report
                  </span>
                  <span
                    className="tw-flex tw-gap-1 tw-cursor-pointer tw-bg-primary-purple-500 tw-text-white tw-border-none tw-rounded-xl tw-text-sm tw-font-semibold tw-px-4 tw-py-2"
                    onClick={() => {
                      if (creditsLeft?.userCredits < 500) {
                        return toast.error("You do not have enough value add credit(s) to request the report. Please reach out to customersuccess@trademo.com for more credits.");
                      } else {
                        setConfirmationModal(true);
                      }
                    }}>
                    <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#ffffff">
                      <path d="M228.31-164q-26.62 0-45.47-18.84Q164-201.69 164-228.31v-503.38q0-26.62 18.84-45.47Q201.69-796 228.31-796h181q-1.23-28.31 19.08-50.15Q448.69-868 480-868q31.69 0 52 21.85 20.31 21.84 18.69 50.15h181q26.62 0 45.47 18.84Q796-758.31 796-731.69v243q-12.87-3.07-25.68-5.11-12.8-2.05-26.32-3.58v-234.31q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H228.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v503.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85h233.31q1.1 14.13 3.05 26.68 1.95 12.55 5.25 25.32H228.31ZM216-240v24-528 246.62-2V-240Zm82-62.62h169.69q3.62-13.61 8.46-26.38 4.85-12.78 10.54-25.61H298v51.99ZM298-454h273.62q19.84-11.92 39.65-21.23t42.27-14.54V-506H298v52Zm0-151.39h364v-51.99H298v51.99Zm182-158.76q10.4 0 17.2-6.8 6.8-6.8 6.8-17.2 0-10.4-6.8-17.2-6.8-6.8-17.2-6.8-10.4 0-17.2 6.8-6.8 6.8-6.8 17.2 0 10.4 6.8 17.2 6.8 6.8 17.2 6.8ZM719.77-65.69q-71.69 0-121.73-50.27Q548-166.22 548-237.92q0-71.69 50.27-121.73 50.26-50.04 121.96-50.04 71.69 0 121.73 50.26Q892-309.16 892-237.47q0 71.7-50.27 121.74-50.26 50.04-121.96 50.04ZM698.31-144h43.38v-72h72v-43.38h-72v-72h-43.38v72h-72V-216h72v72Z" />
                    </svg>
                    Request Report
                  </span>
                </div>
                <span className="tw-italic tw-text-xs tw-font-normal tw-border tw-rounded-lg tw-px-2 tw-flex tw-items-start tw-bg-gray-100 tw-py-1">
                  Once requested, the report will be delivered in 3-5 business days.{" "}
                  <svg xmlns="http://www.w3.org/2000/svg" height="10px" viewBox="0 -960 960 960" width="10px" fill="#101828">
                    <path d="M435-154v-248.31L220.46-278.46l-45-78.08L390-480 175.46-603.46l45-78.08L435-557.69V-806h90v248.31l214.54-123.85 45 78.08L570-480l214.54 123.46-45 78.08L525-402.31V-154h-90Z" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <></>
      )}
      {(creditsLeft.userCredits === 0 || creditsLeft.orgCredits === 0) && !loader && status === undefined ? (
        <>
          <span className="tw-flex tw-justify-between tw-py-4">
            <span className="tw-text-sm tw-font-medium tw-max-w-[65%]">Trademo delivers due diligence reports that provide accurate insights empowering informed decisions across global trade.</span>
            <div className="tw-py-0 tw-px-2.5 tw-text-xs tw-bg-remaining-credits tw-font-bold tw-rounded-none">
              <div className="float-right">
                <img className="px-2 py-1" src="https://res.cloudinary.com/ddfycftwg/image/upload/v1641874517/images/icons/Iconly_Bold_Info_Circle_bwgt7p.svg" alt="..." />
                <span>Remaining Value Add Credit(s) : </span>
                <span className="pr-3">{commaFormat(creditsLeft?.userCredits)}</span>
              </div>
            </div>
          </span>
          <div className="tw-w-full tw-bg-gray-200 tw-border-t"></div>
          <div className="tw-flex tw-justify-center tw-relative tw-top-16">
            <div className="tw-w-[44%] tw-shadow-autoSuggest tw-flex tw-flex-col tw-border tw-border-gray-200 tw-rounded-lg tw-justify-center tw-px-6 tw-py-[14px] tw-gap-3">
              <div className=" tw-flex tw-justify-center tw-bg-get-in-touch tw-w-full  tw-min-h-[120px]">
                <div className="tw-flex tw-items-center tw-justify-center">
                  <img src={`${CLOUDINARY_INTEL_BASE_URL}/v1684478309/intel/icons/Lightning-alt_t9mkjb.svg`} className="tw-w-20 tw-h-20 tw-rounded-2xl" style={{ border: "1px solid white" }} />
                </div>
              </div>
              <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                <span className="tw-text-sm tw-font-medium tw-text-center">{creditsLeft.orgCredits === 0 ? creditsText.organisation : creditsText.user}</span>
                <button className="tw-bg-primary-purple-500 tw-w-fit tw-text-white tw-outline-none tw-border-none tw-text-sm tw-rounded-lg tw-font-semibold tw-px-4 tw-py-2" onClick={() => getInTouchForCredits()}>
                  Get In Touch
                </button>
              </div>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-3 tw-px-4">
              <span className="tw-text-sm tw-font-medium tw-flex tw-flex-col">
                <span>
                  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#5a1ce2">
                    <path d="M852-212 732-332l56-56 120 120-56 56ZM708-692l-56-56 120-120 56 56-120 120Zm-456 0L132-812l56-56 120 120-56 56ZM108-212l-56-56 120-120 56 56-120 120Zm125 92 65-281L80-590l288-25 112-265 112 265 288 25-218 189 65 281-247-149-247 149Z" />
                  </svg>
                </span>
                <span className="tw-text-base tw-font-semibold">Key Highlights:</span>
              </span>
              <div className="tw-w-full tw-bg-gray-200 tw-border-t"></div>
              {dueDiligenceFeatures.map((item) => {
                return (
                  <span key={item} className="tw-flex tw-gap-2 tw-items-center">
                    <span>
                      <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#101828">
                        <path d="m344-60-76-128-144-32 14-148-98-112 98-112-14-148 144-32 76-128 136 58 136-58 76 128 144 32-14 148 98 112-98 112 14 148-144 32-76 128-136-58-136 58Zm94-278 226-226-56-58-170 170-86-84-56 56 142 142Z" />
                      </svg>
                    </span>
                    <span className="tw-text-sm tw-font-normal tw-text-gray-900">{item}</span>
                  </span>
                );
              })}
              <div className="tw-flex">
                <span className="tw-flex tw-gap-1 tw-items-center tw-cursor-pointer tw-border tw-border-gray-200 tw-text-gray-900 tw-text-sm tw-bg-white tw-shadow-button tw-font-semibold tw-rounded-xl tw-px-4 tw-py-2" onClick={() => pdfFileOpener("sample", "pdf", "pdf")}>
                  <span className="material-symbols-outlined tw-w-5">picture_as_pdf</span>
                  View Sample Report
                </span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <></>
      )}
      {userAndOrgChecks.hasOrgRequestedReport && !loader && (
        <div className="tw-flex tw-flex-col tw-gap-4 tw-py-4 tw-px-3">
          <div className="tw-flex tw-items-center tw-justify-between ">
            {status === "delivered" && <span className="tw-text-sm tw-font-medium">{`The due diligence report for ${getTitleCase(countDetails.name)} is ready for download.`}</span>}
            {status === "pending" && <span className="tw-text-sm tw-font-medium">{`The report for this company has already been requested by your organisation`}</span>}
            <div className="tw-py-0 tw-px-2.5 tw-text-xs tw-bg-remaining-credits tw-font-bold tw-rounded-none">
              <div className="float-right">
                <img className="px-2 py-1" src="https://res.cloudinary.com/ddfycftwg/image/upload/v1641874517/images/icons/Iconly_Bold_Info_Circle_bwgt7p.svg" alt="..." />
                <span>Remaining Value Add Credit(s) : </span>
                <span className="pr-3">{commaFormat(creditsLeft?.userCredits)}</span>
              </div>
            </div>
          </div>
          <div className="tw-w-full tw-bg-gray-200 tw-border-t"></div>
          <div className="tw-flex tw-flex-col tw-justify-between tw-w-full tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 tw-gap-6 tw-bg-gray-50 tw-shadow-button">
            <div className="tw-flex tw-w-full tw-justify-between">
              <div className="tw-flex tw-flex-col">
                <span className="tw-text-sm tw-font-medium tw-px-1 tw-py-1">Report Status:</span>
                {/* <div className="tw-flex tw-items-center tw-w-fit tw-bg-semantic-yellow-100 tw-py-0.5 tw-px-2 tw-border tw-rounded-2xl tw-text-sm tw-font-medium tw-gap-1 tw-border-semantic-yellow-900"> */}
                <div className={`${status === "delivered" ? "tw-flex tw-items-center tw-gap-1 tw-py-0.5 tw-px-2 tw-text-sm tw-font-medium tw-rounded-2xl tw-bg-semantic-green-100" : "tw-flex tw-items-center tw-w-fit tw-bg-semantic-yellow-100 tw-py-0.5 tw-px-2 tw-border tw-rounded-2xl tw-text-sm tw-font-medium tw-gap-1 tw-border-semantic-yellow-900"}`}>
                  <span>{getTitleCase(status)}</span>
                  <span className="material-symbols-outlined mr-1">{status === "delivered" ? "check_circle" : "rotate_right"}</span>
                </div>
              </div>
              <div className="tw-flex tw-flex-col">
                <span className="tw-text-sm tw-font-medium tw-px-3 tw-py-1">Requested By:</span>
                <div className="tw-flex tw-items-center tw-w-fit tw-py-1 tw-px-2 tw-text-sm tw-font-medium tw-gap-1">
                  <span className="tw-py-0 tw-px-1 tw-rounded-2xl tw-bg-[#fee4f6]">{getInitials(requestorDetails?.user_name || "NA")}</span>
                  <span>{getTitleCase(requestorDetails?.user_name)}</span>
                </div>
              </div>
              <div className="tw-flex tw-flex-col">
                <span className="tw-text-sm tw-font-medium tw-px-2 tw-py-1">Requested On:</span>
                <div className="tw-flex tw-items-center tw-w-fit tw-py-1 tw-px-2 tw-text-sm tw-font-medium tw-gap-1">
                  <span>{reportDetails.req_date}</span>
                </div>
              </div>
              <div className="tw-flex tw-flex-col">
                <span className="tw-text-sm tw-font-medium tw-px-2 tw-py-1">{status === "delivered" ? "Date of Delivery:" : "Estimated Date of Delivery:"}</span>
                <div className="tw-flex tw-items-center tw-w-fit tw-py-1 tw-px-2 tw-text-sm tw-font-medium tw-gap-1">
                  <span>{status === "delivered" ? reportDetails.delivered_date : reportDetails.est_delivery_date}</span>
                </div>
              </div>
            </div>

            {status === "delivered" && (
              <div className="tw-flex tw-justify-center tw-gap-4">
                <div className="tw-flex tw-flex-col tw-bg-white tw-items-center tw-pt-3 tw-gap-3 tw-h-fit tw-overflow-auto tw-rounded-lg tw-shadow-sideDrawer">
                  <span className="tw-text-sm tw-font-semibold">Complete Report</span>
                  <div className="tw-flex tw-flex-col tw-items-center">
                    <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1747219023/intel/misc/pdf_keytzc.svg" width={"90%"} alt="due-dilligence-pdf" />
                    <button className="tw-flex tw-items-center tw-justify-center tw-w-full tw-gap-1 tw-cursor-pointer tw-bg-primary-purple-500 tw-text-white tw-text-sm tw-font-semibold tw-px-4 tw-py-2" onClick={() => pdfFileOpener("", "", "pdf")}>
                      <span className="material-symbols-outlined tw-w-5">cloud_download</span>
                      Download as PDF
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          {!userAndOrgChecks.hasUserRequestedReport && status === "pending" && (
            <div className="tw-w-full tw-flex tw-justify-center">
              <div className="tw-flex tw-flex-col tw-max-w-[500px] tw-rounded-lg tw-items-center tw-justify-center tw-px-6 tw-py-[14px] tw-gap-3">
                <span className="tw-text-sm tw-font-medium tw-px-6">Trademo delivers due diligence reports that provide accurate insights empowering informed decisions across global trade.</span>
                <div className="tw-flex tw-flex-col tw-gap-3">
                  <span className="tw-text-sm tw-font-medium tw-flex tw-flex-col">
                    <span>
                      <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#5a1ce2">
                        <path d="M852-212 732-332l56-56 120 120-56 56ZM708-692l-56-56 120-120 56 56-120 120Zm-456 0L132-812l56-56 120 120-56 56ZM108-212l-56-56 120-120 56 56-120 120Zm125 92 65-281L80-590l288-25 112-265 112 265 288 25-218 189 65 281-247-149-247 149Z" />
                      </svg>
                    </span>
                    <span className="tw-text-base tw-font-semibold">Key Highlights:</span>
                  </span>
                  <div className="tw-w-full tw-bg-gray-200 tw-border-t"></div>
                  {dueDiligenceFeatures.map((item) => {
                    return (
                      <span key={item} className="tw-flex tw-gap-2 tw-items-center">
                        <span>
                          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#101828">
                            <path d="m344-60-76-128-144-32 14-148-98-112 98-112-14-148 144-32 76-128 136 58 136-58 76 128 144 32-14 148 98 112-98 112 14 148-144 32-76 128-136-58-136 58Zm94-278 226-226-56-58-170 170-86-84-56 56 142 142Z" />
                          </svg>
                        </span>
                        <span className="tw-text-sm tw-font-normal tw-text-gray-900">{item}</span>
                      </span>
                    );
                  })}
                  <div className="tw-flex">
                    <span className="tw-flex tw-gap-1 tw-items-center tw-cursor-pointer tw-border tw-border-gray-200 tw-text-gray-900 tw-text-sm tw-bg-white tw-shadow-button tw-font-semibold tw-rounded-xl tw-px-4 tw-py-2" onClick={() => pdfFileOpener("sample", "pdf", "pdf")}>
                      <span className="material-symbols-outlined tw-w-5">picture_as_pdf</span>
                      View Sample Report
                    </span>
                  </div>
                  <span className="tw-italic tw-text-xs tw-font-normal tw-border tw-rounded-lg tw-px-2 tw-flex tw-items-start tw-bg-gray-100 tw-py-1">
                    Once requested, the report will be delivered in 3-5 business days.{" "}
                    <svg xmlns="http://www.w3.org/2000/svg" height="10px" viewBox="0 -960 960 960" width="10px" fill="#101828">
                      <path d="M435-154v-248.31L220.46-278.46l-45-78.08L390-480 175.46-603.46l45-78.08L435-557.69V-806h90v248.31l214.54-123.85 45 78.08L570-480l214.54 123.46-45 78.08L525-402.31V-154h-90Z" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {isDelivered && !loader && (
        <div className="tw-flex tw-justify-center tw-gap-3 tw-items-center">
          <span className="tw-text-sm tw-font-normal">Request for New Due Diligence Report Generation</span>
          <span className="tw-flex tw-gap-1 tw-items-center tw-cursor-pointer tw-border tw-border-gray-200 tw-text-gray-900 tw-text-sm tw-bg-white tw-shadow-button tw-font-semibold tw-rounded-xl tw-px-4 tw-py-2" onClick={() => onCheck()}>
            <span className="material-symbols-outlined tw-w-5">picture_as_pdf</span>
            Request New Report
          </span>
        </div>
      )}
      {openPopup && getInTouch(creditsLeft.userCredits === 0 ? creditsText.user : creditsText.organisation)}

      <Modal size="md" centered show={confirmationModal} onHide={() => setConfirmationModal(false)} animation={false} className="confirm-modal">
        <Modal.Header>
          <div className="w-100 d-flex">
            <div className="col-md-6 m-auto">
              <span className="header-confirm">Confirmation</span>
            </div>
            <div className="col-md-6 text-right pr-0">
              <button
                className="btn btn-primary trademo-new-btn mx-2 px-2"
                onClick={() => {
                  onRequest();
                  setConfirmationModal(false);
                }}>
                Confirm
              </button>
              <button type="button" className="close trademo-modal-close" aria-label="Close" onClick={() => setConfirmationModal(false)}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <p className="modaltext mb-0">
            You are requesting <strong>1 Due Diligence Report</strong>, which will cost you <strong>500 Value Add Credit(s)</strong>
          </p>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default DueDiligenceReports;
